import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  NgZone,
  EventEmitter,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarConfig as MatSnackBarConfig,
} from '@angular/material/legacy-snack-bar';
import { ClassroomAssignmentService } from '@app/infrastructure/core/services/api/classroom-assignment.service';
import {
  AssignmentStatusEnum,
  IClassroomAssignmentDayUnitListItemDTO,
  IClassroomAssignmentListItemDTO,
} from '@app/infrastructure/models/classroom-assignment';
import { StudentAssignmentStatus } from '@app/infrastructure/models/student-assignment';
import { SaveCancelService } from '@app/infrastructure/core/services/state/save-cancel.service';

import { MessageSnackbarComponent } from '../../message-snackbar/message-snackbar.component';
import { StatusEnum } from '@app/infrastructure/models/status';
import { SaveCancelButtonConfig } from '@app/infrastructure/models/form/button';
import { ConfirmDialogConfig } from '@app/infrastructure/models/dialog';
import { ModalService } from '@app/infrastructure/core/services/modal.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

@Component({
  selector: 'app-grade-unit-day-assignment-card',
  templateUrl: './grade-unit-day-assignment-card.component.html',
  styleUrls: ['./grade-unit-day-assignment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GradeUnitDayAssignmentCardComponent implements OnInit {
  @Input() classroomId: string;
  @Input() gradeUnitDay: IClassroomAssignmentDayUnitListItemDTO;
  @Input() displayActions: boolean = true;
  @Input() unitNumber: number;
  @Input() gradeNumber: string;
  @Input() isTeacher: boolean;
  @Input() allowEdit: boolean = true;
  @Input() dayTitle: string = '';
  @Input() pageTitle: string = '';
  @Output() refresh: EventEmitter<boolean> = new EventEmitter();
  gradeUnitDaysLink: string;
  queryParams: { assignmentId: number } = { assignmentId: null };
  @Input() showDueDate: boolean = true;
  @Input() progressValue: string = '';
  @Input() isLink: boolean = true;
  @Input() isAssigned: boolean = false;
  @Input() status: string = '';
  @Input() dueDateStatus: string = '';
  @Input() isAssignment: boolean = true;
  public dayDescription: string = '';
  toggleDatePicker: boolean = false;
  studentAssignmentStatus: StudentAssignmentStatus;

  assignmentForm = this.formBuilder.group({
    dueDate: '',
  });

  constructor(
    private snackBar: MatSnackBar,
    private zone: NgZone,
    private assignmentService: ClassroomAssignmentService,
    private formBuilder: UntypedFormBuilder,
    private saveCancelService: SaveCancelService,
    private modalService: ModalService,
    private cd: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (
      this.gradeUnitDay.gradeUnitDayStatus === AssignmentStatusEnum.UNASSIGNED
    ) {
      this.dueDateStatus = 'Assign Due Date';
      this.isAssigned = false;
    } else this.isAssigned = true;
    this.getProgressValue();
    this.gradeUnitDaysLink = `${this.gradeUnitDay.dayNumber}`;
    this.pageTitle = this.getPageTitleText();
    this.dayTitle = `Day ${this.gradeUnitDaysLink}`;
    this.queryParams.assignmentId = this.gradeUnitDay.classroomAssignmentId;
    this.dayDescription = this.gradeUnitDay.dayTitle;
    if (this.isAssigned) this.displayActions = false;
  }

  getProgressValue() {
    const enrolledStudents = this.gradeUnitDay.enrolledStudents?.length;
    const completedAssignments = this.gradeUnitDay.studentAssignment?.filter(
      (studentAssignment) => studentAssignment.completedAt,
    );
    this.progressValue = `${
      (completedAssignments?.length / enrolledStudents) * 100
    }`;
    this.getAssignmentStatus(+this.progressValue);
  }

  getAssignmentStatus(value: number) {
    const today = new Date(new Date().setHours(0, 0, 0, 0));
    if (
      this.gradeUnitDay.gradeUnitDayStatus === AssignmentStatusEnum.UNASSIGNED
    ) {
      this.status = StatusEnum.UNASSIGNED;
      this.dueDateStatus = 'Assign Due Date';
      return;
    }
    if (value > 0 && value < 100) this.status = StatusEnum.INPROGRESS;
    if (value === 100) this.status = StatusEnum.COMPLETED;
    if (!value) this.status = StatusEnum.ASSIGNED;
    if (value < 100 && today > new Date(this.gradeUnitDay.assignmentDueDate))
      this.status = StatusEnum.WRAP_UP;

    this.dueDateStatus = 'Due Date';
  }

  onSettingsClick($event) {
    $event.stopImmediatePropagation();
    $event.preventDefault();
    if (!this.allowEdit) return;

    this.displayActions = !this.displayActions;
  }

  loadCards() {
    this.refresh.emit(true);
  }

  getImgSrc(): string {
    return this.gradeUnitDay.dayCoverImageUrl;
  }

  getAllowEdit(): boolean {
    return this.allowEdit;
  }

  getAlt(): string {
    return `STEMTaught ${this.gradeNumber} Unit ${this.unitNumber}`;
  }

  actionsVisible(): boolean {
    return this.displayActions;
  }

  getStatusName(): string {
    switch (this.status) {
      case StatusEnum.ASSIGNED:
        return 'assigned';
      case StatusEnum.COMPLETED:
        return 'completed';
      case StatusEnum.WRAP_UP:
        return 'wrapup';
      case StatusEnum.UNASSIGNED:
        return 'unassigned';
      case StatusEnum.INPROGRESS:
        return 'in-progress';
      default:
        return 'unassigned';
    }
  }

  getColorSchemeStyle(): string {
    const status = this.getStatusName();
    if (this.isAssignment) {
      return `--foreground: var(--${status}-foreground);--background: var(--${status}-background);--progress: var(--${status}-progress)`;
    } else {
      return '--foreground: var(--wrapup-foreground);--background: var(--wrapup-background);--progress: var(--wrapup-progress);';
    }
  }

  prevent($event: MouseEvent) {
    $event.stopImmediatePropagation();
    $event.preventDefault();
  }

  openEdit() {
    if (!this.allowEdit) return;
    this.toggleDatePicker = true;
    this.displayActions = true;
  }

  closeEdit() {
    if (!this.allowEdit) return;
    this.toggleDatePicker = false;
    this.displayActions = false;
  }

  assignDate($event: MouseEvent) {
    if (!this.allowEdit) return;
    this.displayActions = true;
  }

  async unassignAssignment(event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.allowEdit) return;

    let success = false;
    let msg: string;
    const asnId = !!this.gradeUnitDay.assignmentId
      ? this.gradeUnitDay.assignmentId
      : this.gradeUnitDay.classroomAssignmentId;
    try {
      if (this.isAssigned) {
        this.assignmentService
          .deleteAssignment(this.classroomId, asnId)
          .subscribe((result) => {
            this.loadCards();
          });
        msg = 'Assignment Successfully Deleted';
        success = true;
      } else {
        msg = 'Unassigned day cannot be deleted';
      }
    } catch (err) {
      msg = `An error occurred attempting to delete assignment ${err}`;
    } finally {
      if (success) {
        this.showSuccess([msg]);
      } else {
        this.showError([msg]);
      }
      this.closeEdit();
      this.isAssigned = false;
      this.status = StatusEnum.UNASSIGNED;
      this.dueDateStatus = 'Assign Due Date';
      this.displayActions = true;
    }
  }

  async onAssignClick(event: any) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.allowEdit) return;

    const asnId = !!this.gradeUnitDay.assignmentId
      ? this.gradeUnitDay.assignmentId
      : this.gradeUnitDay.classroomAssignmentId;

    try {
      const classAssignment: IClassroomAssignmentListItemDTO = {
        assignmentId: asnId,
        classroomId: this.classroomId,
        gradeUnitDayId: this.gradeUnitDay.gradeUnitDayId,
        gradeUnitId: this.gradeUnitDay.gradeUnitId,
        dueDate: new Date(this.gradeUnitDay.assignmentDueDate),
      };

      if (!this.isAssigned) {
        // Create new assignment
        this.assignmentService
          .assignGradeUnitDayToClassroom(classAssignment)
          .subscribe(
            (response) => {
              this.showSuccess(['Day Successfully Assigned']);
              this.loadCards();
              this.isAssigned = true;
              this.status = StatusEnum.ASSIGNED;
              this.dueDateStatus = 'Starting';
              this.closeEdit();
            },
            (err) => this.handleError(err),
          );
      } else {
        // Update existing assignment
        this.assignmentService
          .updateAssignGradeUnitDayToClassroom(classAssignment)
          .subscribe(
            (response) => {
              this.showSuccess(['Day Successfully Updated']);
              this.loadCards();
              this.closeEdit();
              this.getProgressValue();
            },
            (err) => this.handleError(err),
          );
      }
    } catch (err) {
      this.showError([`An error occurred attempting to assign day ${err}`]);
    }
  }

  public showSuccess(message: string[]): void {
    const configSuccess: MatSnackBarConfig<MessageSnackbarComponent> = {
      panelClass: 'success',
      verticalPosition: 'bottom',
      duration: 3000,
    };
    this.openSnackbar(message, configSuccess);
  }

  public showError(message: string[]): void {
    const configError: MatSnackBarConfig<MessageSnackbarComponent> = {
      panelClass: 'error',
      verticalPosition: 'top',
      duration: 10000,
    };
    this.openSnackbar(message, configError);
  }

  private openSnackbar(
    message: string[],
    config: MatSnackBarConfig<MessageSnackbarComponent>,
  ) {
    // Wrap snackbar call in zone invocation to fix rendering inconsistencies
    this.zone.run(() => {
      this.saveCancelService.setDisabledState(false);
      this.snackBar.openFromComponent(MessageSnackbarComponent, {
        data: message,
        ...config,
      });
    });
  }

  public getPageTitleText(): string {
    const { dayNumber, dayEndPage, dayStartPage, isQuiz } = this.gradeUnitDay;
    if (dayNumber >= 0) {
      if (isQuiz) {
        return 'Assessment';
      } else if (dayStartPage && dayEndPage) {
        if (isNaN(Number(dayStartPage)) && isNaN(Number(dayEndPage)))
          return `${dayStartPage}  ${dayEndPage}`;
        return `Pages ${this.gradeUnitDay.dayStartPage}-${this.gradeUnitDay.dayEndPage}`;
      } else if (dayStartPage) {
        if (isNaN(Number(dayStartPage))) return dayStartPage;
        return `Page ${this.gradeUnitDay.dayStartPage}`;
      } else {
        return ``;
      }
    }
  }

  public openUnassignDialog(event: any): void {
    const dialogConfig = new ConfirmDialogConfig({
      id: 0,
      title: 'Are you sure you want to unassign?',
      messageList: [
        'Any student progress will be lost if the assignment is unassigned.',
      ],
      submit: new SaveCancelButtonConfig({
        save: 'Unassign',
      }),
    });
    this.modalService
      .openConfirmDialog(dialogConfig)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.unassignAssignment(event);
        } else {
          this.closeEdit();
          this.cd.markForCheck();
        }
      });
  }

  private handleError(error: HttpErrorResponse): void {
    if (error.status === 400) {
      const snackBarRef = this.snackBar.open(
        'Assignment due date does not fall within a grading period. Create at grading period?',
        'Create',
        { panelClass: 'error', verticalPosition: 'top', duration: 10000 },
      );
      snackBarRef
        .onAction()
        .subscribe(() => this.redirectToGradingPeriodCreation());
    }
  }

  private redirectToGradingPeriodCreation(): void {
    this.router.navigate([
      'teacher',
      'classes',
      this.classroomId,
      'grading-period',
    ]);
  }
}
